// @ts-nocheck
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@store/Overrides/Components/ProductPartNumbers";
import { inlineSeparatorStyles } from "@store/Themes/common/mixins/typography";
import { attributeSpacing } from "@store/Themes/common/props/attributes";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import React, { FC, useContext } from "react";
import { css } from "styled-components";

/* Store Custom Code - Start */
import { getMultiLocationCatalogResult } from "@store/Framework/Common/Utilities/multiLocationCatalogUtilities";
import AvailableAtOtherShipToLabel, {
    AvailableAtOtherShipToLabelPresentationProps,
} from "@store/Library/Components/MultiLocationCatalog/AvailableAtOtherShipToLabel";
import NotInYourCatalogBadge, {
    NotInYourCatalogBadgePresentationProps,
} from "@store/Library/Components/NotInYourCatalogBadge";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import getBooleanProperty from "@toolkit/Framework/Common/Utilities/getBooleanProperty";
/* Store Custom Code - End */

const enum fields {
    showLabel = "showLabel",
    showManufacturingNumber = "showManufacturingNumber",
    showCustomerPartNumber = "showCustomerPartNumber",
}

interface Props extends WidgetProps {
    fields: {
        [fields.showLabel]: boolean;
        [fields.showManufacturingNumber]: boolean;
        [fields.showCustomerPartNumber]: boolean;
    };
}

interface ProductNumbersStyles {
    productPartNumbers?: ProductPartNumbersStyles;
    // Store Custom Code
    availableAtOtherShipToLabel?: AvailableAtOtherShipToLabelPresentationProps;
    notInYourCatalogBadge?: NotInYourCatalogBadgePresentationProps;
}

/* Store Custom Code - Start */
const badgeWrapperProps = {
    extendedStyles: {
        wrapper: {
            css: css`
                ${spacingStyles(null, pxToRem(10))}
            `,
        },
    },
};
/* Store Custom Code - End */

const productNumbersStyle: ProductNumbersStyles = {
    productPartNumbers: {
        wrapper: {
            css: css`
                ${x => spacingStyles(pxToRem(3), x.theme.design.product.detail.contentSpacing) /* Store Custom Code */}
                display: flex;
                flex-wrap: wrap;
                row-gap: ${attributeSpacing};
                /* Store Custom Code - Start */
                line-height: ${x => x.theme.design.typography.lineHeight.base};
                /* Store Custom Code - End */
            `,
        },
        innerWrapper: {
            css: css`
                ${inlineSeparatorStyles({ spacing: "1em", color: "text.accent" })}
                margin-bottom: 0;
            `,
        },
    },
    /* Store Custom Code - Start */
    availableAtOtherShipToLabel: badgeWrapperProps,
    notInYourCatalogBadge: badgeWrapperProps,
    /* Store Custom Code - End */
};

const ProductDetailsPartNumbers: FC<Props> = ({
    fields: { showLabel, showManufacturingNumber, showCustomerPartNumber },
}) => {
    const { product } = useContext(ProductContext);

    /* Store Custom Code - Start */
    const multiLocationCatalogResult = getMultiLocationCatalogResult(product);
    const isMLCProduct = Boolean(multiLocationCatalogResult?.isMultiLocationCatalogProduct);
    const isProductNotInCatalog = getBooleanProperty(product.properties?.notInCatalog);
    /* Store Custom Code - End */

    return (
        <>
            {/* Store Custom Code - Start */}
            {isMLCProduct && <AvailableAtOtherShipToLabel {...productNumbersStyle.availableAtOtherShipToLabel} />}
            {!isMLCProduct && isProductNotInCatalog && (
                <NotInYourCatalogBadge {...productNumbersStyle.notInYourCatalogBadge} />
            )}
            {/* Store Custom Code - End */}
            <ProductPartNumbers
                productNumber={product.productNumber}
                customerProductNumber={product.customerProductNumber}
                manufacturerItem={product.manufacturerItem}
                showCustomerName={showCustomerPartNumber && !isMLCProduct} // Store Custom Code
                showLabel={showLabel}
                showManufacturerItem={showManufacturingNumber}
                extendedStyles={productNumbersStyle.productPartNumbers}
            />
        </>
    );
};

const widgetModule: WidgetModule = {
    component: ProductDetailsPartNumbers,
    definition: {
        group: "Product Details",
        displayName: "Part Numbers",
        allowedContexts: ["ProductDetailsPage"],
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.showLabel,
                displayName: "Show Labels",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.showManufacturingNumber,
                displayName: "Show Manufacturing Part Number",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.showCustomerPartNumber,
                displayName: "Show Customer Part Number",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
